import { useState } from "react";
import { normalizeAppPassword } from "../../shared/app-password";
import type { IcloudServiceView, IcloudView } from "../../shared/contracts";
import { ApiError, api, errorText } from "../lib/api";
import { fmtRelative } from "../lib/format";
import { useNow } from "../lib/hooks";
import { Badge, Button, Field, Input, Notice, Switch } from "./ui";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const APPLE_DOMAINS = /@(?:icloud|me|mac)\.com$/i;
const NOT_APP_PASSWORD =
  "Ce n'est pas un mot de passe pour application : Apple les affiche en quatre groupes de quatre lettres séparés par des tirets. N'entre jamais ton mot de passe Apple habituel.";

/**
 * Connexions → iCloud: a short guide to create an app-specific password at Apple, the form (Apple ID, iCloud Mail
 * address, password, services), then the connected state with a switch per service. The password is only ever sent
 * once to POST /api/icloud and cleared from the page right after.
 */
export function IcloudSection({ data, onChange }: { data: IcloudView; onChange: () => unknown }) {
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "info"; text: string } | null>(null);

  return (
    <div id="icloud" data-testid="icloud-card" className="scroll-mt-24 py-[11px]">
      <div className="flex min-w-0 items-center gap-3">
        <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-inset font-display text-[17px] text-fg">
          i
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] leading-snug font-semibold sm:truncate">iCloud</p>
          <p className="text-[12.5px] leading-snug text-subtle sm:truncate">{!data.available ? "Pas disponible ici" : data.connected ? data.appleId : "Calendrier et Mail"}</p>
        </div>
        {data.available && !data.connected && !editing ? (
          <Button size="sm" onClick={() => setEditing(true)} className="md:h-8 md:px-3.5">
            Connecter
          </Button>
        ) : null}
      </div>
      {!data.available ? null : data.connected && !editing ? (
        <div className="mt-3">
          <IcloudConnected data={data} onChange={onChange} onEdit={() => setEditing(true)} onDisconnected={(t) => setNotice({ tone: "info", text: t })} />
        </div>
      ) : editing ? (
        <div className="mt-3 rounded-[16px] bg-bg p-4">
          <IcloudForm
            initialAppleId={data.appleId ?? ""}
            initialMail={data.mailAddress ?? ""}
            onCancel={() => setEditing(false)}
            onConnected={() => {
              setEditing(false);
              setNotice({ tone: "success", text: "iCloud est connecté. Je t'écris pour te proposer de surveiller ton calendrier et tes mails." });
              onChange();
            }}
          />
        </div>
      ) : null}
      {notice ? (
        <Notice tone={notice.tone} role="status" className="mt-3">
          {notice.text}
        </Notice>
      ) : null}
    </div>
  );
}

function IcloudForm({ initialAppleId, initialMail, onCancel, onConnected }: { initialAppleId: string; initialMail: string; onCancel?: () => void; onConnected: () => void }) {
  const [appleId, setAppleId] = useState(initialAppleId);
  const [mailAddress, setMailAddress] = useState(initialMail);
  const [mailTouched, setMailTouched] = useState(Boolean(initialMail));
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [mail, setMail] = useState(true);
  const [calendar, setCalendar] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: "appleId" | "mail" | "password"; text: string } | null>(null);

  const effectiveMail = mailTouched ? mailAddress : APPLE_DOMAINS.test(appleId.trim()) ? appleId.trim() : mailAddress;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    const id = appleId.trim().toLowerCase();
    if (!EMAIL.test(id)) return setFieldError({ field: "appleId", text: "Indique l'adresse e-mail de ton identifiant Apple." });
    if (!mail && !calendar) return setError("Active au moins un service : Calendrier ou Mail.");
    if (mail && !EMAIL.test(effectiveMail.trim())) return setFieldError({ field: "mail", text: "Indique ton adresse iCloud Mail (…@icloud.com, @me.com ou @mac.com)." });
    const pw = normalizeAppPassword(password);
    if (!pw) return setFieldError({ field: "password", text: NOT_APP_PASSWORD });
    setBusy(true);
    try {
      await api("POST /api/icloud", { body: { appleId: id, mailAddress: mail ? effectiveMail.trim().toLowerCase() : null, password: pw, mail, calendar } });
      setPassword("");
      onConnected();
    } catch (err) {
      setPassword("");
      const code = err instanceof ApiError ? err.code : "";
      if (code === "icloud_not_app_password" || code === "icloud_auth_failed") setFieldError({ field: "password", text: errorText(err) });
      else if (code === "icloud_mail_address") setFieldError({ field: "mail", text: errorText(err) });
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-4 text-[13.5px]" onSubmit={(e) => void submit(e)} noValidate data-testid="icloud-form">
      <ol className="list-decimal space-y-1 pl-5 text-muted">
        <li>
          Ouvre{" "}
          <a className="font-medium text-accent underline" href="https://account.apple.com/" target="_blank" rel="noopener noreferrer">
            account.apple.com
          </a>{" "}
          et connecte-toi.
        </li>
        <li>Dans « Connexion et sécurité », choisis « Mots de passe pour applications ».</li>
        <li>Génère-en un nommé « Pulsez » et colle-le ci-dessous.</li>
      </ol>
      <p className="text-[12.5px] text-muted">
        Jamais ton mot de passe Apple habituel.{" "}
        <a className="underline" href="https://support.apple.com/fr-fr/102654" target="_blank" rel="noopener noreferrer">
          Aide d'Apple
        </a>
      </p>
      <Field label="Identifiant Apple (adresse e-mail)" htmlFor="icloud-apple-id" error={fieldError?.field === "appleId" ? fieldError.text : null}>
        <Input id="icloud-apple-id" type="email" autoComplete="username" value={appleId} onChange={(e) => setAppleId(e.target.value)} />
      </Field>
      {mail ? (
        <Field
          label="Adresse iCloud Mail"
          htmlFor="icloud-mail-address"
          hint="…@icloud.com, @me.com ou @mac.com"
          error={fieldError?.field === "mail" ? fieldError.text : null}
        >
          <Input
            id="icloud-mail-address"
            type="email"
            autoComplete="email"
            value={effectiveMail}
            onChange={(e) => {
              setMailTouched(true);
              setMailAddress(e.target.value);
            }}
          />
        </Field>
      ) : null}
      <Field label="Mot de passe pour application" htmlFor="icloud-password" error={fieldError?.field === "password" ? fieldError.text : null}>
        <div className="flex gap-2">
          <Input
            id="icloud-password"
            type={show ? "text" : "password"}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="••••-••••-••••-••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="button" variant="ghost" onClick={() => setShow((v) => !v)}>
            {show ? "Masquer" : "Afficher"}
          </Button>
        </div>
      </Field>
      <div className="space-y-2">
        <Switch id="icloud-calendar" label="Calendrier" description="Lire et gérer ton agenda iCloud." checked={calendar} onChange={setCalendar} />
        <Switch id="icloud-mail" label="Mail" description="Lire et ranger tes mails. Rien ne part sans ton accord." checked={mail} onChange={setMail} />
      </div>
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" busy={busy}>
          Connecter iCloud
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuler
          </Button>
        ) : null}
        {busy ? <span className="text-muted" aria-live="polite">Vérification auprès d'Apple…</span> : null}
      </div>
    </form>
  );
}

function ServiceRow({ title, name, view, children, busy, onToggle }: { title: string; name: string; view: IcloudServiceView; children?: React.ReactNode; busy: boolean; onToggle: (on: boolean) => void }) {
  const now = useNow(60_000);
  const state =
    view.state === "invalid" ? (
      <Badge tone="danger">Mot de passe refusé</Badge>
    ) : view.state === "active" ? (
      <span className="text-muted">Actif{view.lastSyncAt ? ` · synchronisé ${fmtRelative(view.lastSyncAt, now)}` : ""}</span>
    ) : (
      <span className="text-muted">Désactivé</span>
    );
  return (
    <div className="rounded-2xl bg-sunken px-3.5 py-1" data-testid={`icloud-service-${name}`}>
      <Switch id={`icloud-toggle-${name}`} label={title} checked={view.enabled} disabled={busy} onChange={onToggle} />
      <div className="-mt-1 space-y-1 pb-2.5 text-[12.5px]">
        <p>{state}</p>
        {view.state === "invalid" ? <p className="text-danger">Apple refuse le mot de passe pour application : crée-en un nouveau et reconnecte iCloud.</p> : null}
        {view.lastError && view.lastError.code !== "auth_invalid" && view.state !== "off" ? (
          <p className="text-subtle">
            Dernier souci : {view.lastError.message} ({fmtRelative(view.lastError.at, now)})
          </p>
        ) : null}
        {view.enabled ? <p className="text-subtle">{view.watched ? "Surveillé" : `Pour que je le surveille, écris-moi « surveille ${title === "Mail" ? "iCloud Mail" : "Calendrier iCloud"} »`}</p> : null}
        {children}
      </div>
    </div>
  );
}

function IcloudConnected({ data, onChange, onEdit, onDisconnected }: { data: IcloudView; onChange: () => unknown; onEdit: () => void; onDisconnected: (text: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle(service: "mail" | "calendar", on: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api("PATCH /api/icloud", { body: { [service]: on } });
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    setError(null);
    try {
      await api("DELETE /api/icloud");
      setConfirm(false);
      onDisconnected(
        "iCloud est déconnecté et le mot de passe est effacé de Pulsez. Pense aussi à le révoquer chez Apple : account.apple.com → Connexion et sécurité → Mots de passe pour applications → « Pulsez » → Supprimer.",
      );
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 text-[13.5px]">
      <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-success-ink">
        <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-success" />
        Connecté
      </p>
      <ServiceRow title="Calendrier" name="calendar" view={data.calendar} busy={busy} onToggle={(on) => void toggle("calendar", on)}>
        {data.calendar.enabled && data.calendar.calendars.length ? (
          <p className="flex flex-wrap gap-x-3 gap-y-1 text-muted">
            {data.calendar.calendars.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: c.color && /^#[0-9a-f]{3,8}$/i.test(c.color) ? c.color : "currentColor" }} />
                {c.name}
              </span>
            ))}
          </p>
        ) : data.calendar.enabled ? (
          <p className="text-muted">Aucun calendrier d'événements trouvé.</p>
        ) : null}
      </ServiceRow>
      <ServiceRow title="Mail" name="mail" view={data.mail} busy={busy} onToggle={(on) => void toggle("mail", on)}>
        {data.mail.enabled && data.mailAddress ? <p className="text-muted">Adresse : {data.mailAddress}</p> : null}
      </ServiceRow>
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {confirm ? (
        <div className="rounded-2xl bg-sunken p-3.5">
          <p>Pulsez oubliera ton mot de passe pour application et arrêtera de lire ton calendrier et tes mails iCloud.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="danger" busy={busy} onClick={() => void disconnect()}>
              Déconnecter iCloud
            </Button>
            <Button variant="ghost" onClick={() => setConfirm(false)}>
              Annuler
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={onEdit}>
            Changer le mot de passe
          </Button>
          <Button variant="ghost" onClick={() => setConfirm(true)}>
            Déconnecter
          </Button>
        </div>
      )}
    </div>
  );
}
